import React, { useState } from "react"
import PropTypes from "prop-types"
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Stack,
  Checkbox,
} from "@mui/material"
import PartidaAccordion from "./PartidaAccordion"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

const ChapterAccordion = ({
  chapter,
  selectedItems = {},
  onSelect,
  parentChapters = [],
}) => {
  const [expanded, setExpanded] = useState(false)
  const handleChange = (event, isExpanded) => {
    setExpanded(isExpanded ? chapter.chapterId : false)
  }

  // Indeterminate logic for chapters: check direct children (chapters and partidas)
  let checked = !!selectedItems[chapter.chapterId]
  let indeterminate = false
  let childIds = []
  if (Array.isArray(chapter.chapters)) {
    childIds = childIds.concat(chapter.chapters.map(sub => sub.chapterId))
  }
  if (Array.isArray(chapter.partidas)) {
    childIds = childIds.concat(
      chapter.partidas.map(partida => partida.partidaId),
    )
  }
  if (childIds.length > 0) {
    const checkedCount = childIds.filter(id => !!selectedItems[id]).length
    checked = checkedCount === childIds.length && childIds.length > 0
    indeterminate = checkedCount > 0 && checkedCount < childIds.length
  }
  // Handler para checkbox capítulo
  const handleCheckboxClick = e => {
    e.stopPropagation()
    if (onSelect) {
      onSelect("chapter", chapter, parentChapters)
    }
  }

  return (
    <Accordion
      expanded={expanded === chapter.chapterId}
      onChange={handleChange}
      sx={{
        mb: 0.5,
        boxShadow: "none",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel-${chapter.chapterId}-content`}
        id={`panel-${chapter.chapterId}-header`}
        sx={{
          backgroundColor: "grey.300",
          "&:hover": { backgroundColor: "#c8c8c8" },
          "& .MuiAccordionSummary-expandIconWrapper": {
            marginLeft: 1,
            marginRight: 0,
          },
          "& .MuiAccordionSummary-content": {
            margin: "6px 0 !important",
            minWidth: 0,
            flex: 1,
          },
          minHeight: "36px !important",
          height: "36px",
          "&.Mui-expanded": {
            minHeight: "36px !important",
            height: "36px",
            marginBottom: "4px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Checkbox
            size="small"
            sx={{ p: 0, mr: 1 }}
            checked={checked}
            indeterminate={indeterminate}
            onClick={handleCheckboxClick}
            onFocus={e => e.stopPropagation()}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: "medium",
              fontSize: "0.9rem",
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              width: "100%",
              maxWidth: "100%",
            }}
          >
            {chapter.name}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1, pt: 0.5 }}>
        {Array.isArray(chapter.chapters) && chapter.chapters.length > 0 && (
          <Stack spacing={0.5}>
            {chapter.chapters.map(subchapter => (
              <ChapterAccordion
                key={subchapter.chapterId}
                chapter={subchapter}
                selectedItems={selectedItems}
                onSelect={onSelect}
                parentChapters={[chapter, ...parentChapters]}
              />
            ))}
          </Stack>
        )}
        {Array.isArray(chapter.partidas) && chapter.partidas.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {chapter.partidas.map(partida => (
                <PartidaAccordion
                  key={partida.partidaId}
                  partida={partida}
                  selectedItems={selectedItems}
                  onSelect={onSelect}
                  parentChapters={[chapter, ...parentChapters]}
                />
              ))}
            </Stack>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

ChapterAccordion.propTypes = {
  chapter: PropTypes.shape({
    chapterId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    chapters: PropTypes.array,
    partidas: PropTypes.array,
  }).isRequired,
  selectedItems: PropTypes.object,
  onSelect: PropTypes.func,
  parentChapters: PropTypes.array,
}

export default ChapterAccordion
